import { StripDiv } from 'components/ModeStrip/style';
import CustomTooltip from 'components/Basic/Tooltip';
import { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setFlattenShape, setSelectedShapeId } from 'store/slices/TreeSlice';
import { selectFlattenShape, selectTreeDoneLoading } from 'store/slices/TreeSlice/selector';
import { ShapeBtn, ShapeBtnWrap } from './style';

type FlattenShapeAction = 'polygon' | 'circle' | 'free';

const INACTIVE_COLOR = 'var(--defaultIconColor)';
const ACTIVE_COLOR = 'var(--primary)';

const FlattenShapeActions: Array<{ label: string; val: FlattenShapeAction }> = [
	{ label: 'Free Draw', val: 'free' },
	{ label: 'Circle', val: 'circle' },
	{ label: 'Polygon', val: 'polygon' },
];

const CircleIcon = ({ active }: { active: boolean }) => {
	const stroke = active ? ACTIVE_COLOR : INACTIVE_COLOR;
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 22 22" fill="none">
			<circle cx="11" cy="11" r="10" stroke={stroke} strokeWidth="1.7" />
		</svg>
	);
};

const PolygonIcon = ({ active }: { active: boolean }) => {
	const stroke = active ? ACTIVE_COLOR : INACTIVE_COLOR;
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="19.2" height="21" viewBox="0 0 24 26" fill="none">
			<path
				d="M21.3083 8.61406V17.9217M18.9385 5.62012L10.3083 3.23633M7.00203 4.80637L4.07183 9.46021M3.53906 13.5417L6.82348 20.8833M10.3083 22.4387L18.7698 20.5722"
				stroke={stroke}
				strokeWidth="1.6"
				strokeLinejoin="round"
			/>
			<circle cx="8.19351" cy="3.11538" r="2.11538" stroke={stroke} strokeWidth="1.6" />
			<circle cx="20.8849" cy="6.4982" r="2.11538" stroke={stroke} strokeWidth="1.6" />
			<circle cx="20.8849" cy="20.0373" r="2.11538" stroke={stroke} strokeWidth="1.6" />
			<circle cx="8.19351" cy="22.5763" r="2.11538" stroke={stroke} strokeWidth="1.6" />
			<circle cx="3.11538" cy="11.5763" r="2.11538" stroke={stroke} strokeWidth="1.6" />
		</svg>
	);
};

const FreeDrawIcon = ({ active }: { active: boolean }) => {
	const stroke = active ? ACTIVE_COLOR : INACTIVE_COLOR;
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="15.6" height="20" viewBox="0 0 18 23" fill="none">
			<path
				d="M1.56233 17.7391C0.500961 19.2701 -0.170599 21.6585 5.63415 18.9645C9.8939 16.9876 11.1164 17.1206 11.2111 17.7064C11.2951 18.2255 10.7392 18.5967 10.3304 18.9275C9.04071 19.971 7.68576 21.4108 9.52263 21.3381C12.205 21.232 15.1962 18.9645 16.3347 19.6978"
				stroke={stroke}
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
			<path
				d="M12.004 1.25415L2.99219 12.6795C3.7404 13.881 5.45788 14.9386 6.2231 15.3172L15.4092 3.89223C15.7847 3.42532 15.6995 2.74044 15.2211 2.37972L13.4763 1.06401C13.0155 0.716497 12.3614 0.80096 12.004 1.25415Z"
				stroke={stroke}
				strokeWidth="1.7"
			/>
			<path
				d="M11.0625 2.76404L14.1212 5.24379"
				stroke={stroke}
				strokeWidth="1.7"
			/>
			<path
				d="M5.78641 15.0185C4.80779 14.4922 3.93052 13.7038 3.34819 13.0848C3.12688 12.8495 2.71224 12.9431 2.63501 13.2567L1.47656 17.961L5.80355 15.6566C6.05142 15.5246 6.03374 15.1515 5.78641 15.0185Z"
				stroke={stroke}
				strokeWidth="1.7"
			/>
			<path
				d="M3.23001 16.6792L2.07852 15.5381L1.64062 17.9222L3.23001 16.6792Z"
				fill={stroke}
			/>
		</svg>
	);
};

const ShapeIcon = ({ shape, active }: { shape: FlattenShapeAction; active: boolean }) => {
	if (shape === 'circle') return <CircleIcon active={active} />;
	if (shape === 'free') return <FreeDrawIcon active={active} />;
	return <PolygonIcon active={active} />;
};

export default function FlattenShapeStrip() {
	const dispatch = useDispatch();
	const flattenShape = useSelector(selectFlattenShape);
	const draftPointsCount = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points.length);
	const isDoneLoading = useSelector(selectTreeDoneLoading);
	const drawingInProgress = draftPointsCount > 0;
	const isDisabled = drawingInProgress || isDoneLoading;

	const selectShape = (shape: FlattenShapeAction) => {
		if (shape === flattenShape) return;
		if (isDisabled) return;
		dispatch(setSelectedShapeId(null));
		dispatch(setFlattenShape(shape));
	};

	return (
		<StripDiv className='flatten-strip'>
			<ShapeBtnWrap $disabled={isDisabled}>
				{FlattenShapeActions.map((shape, index) => (
					<Fragment key={shape.val}>
						<ShapeBtn
							id={`flatten-shape-${shape.val}`}
							$disabled={isDisabled}
							$isLast={index === FlattenShapeActions.length - 1}
						>
							<input
								type="radio"
								name="flatten-shape"
								checked={shape.val === flattenShape}
								onChange={() => selectShape(shape.val)}
								disabled={isDisabled}
							/>
							<ShapeIcon shape={shape.val} active={shape.val === flattenShape} />
						</ShapeBtn>
						<CustomTooltip
							elementID={`flatten-shape-${shape.val}`}
							content={shape.label}
							place="bottom"
							positionStrategy="fixed"
						/>
					</Fragment>
				))}
			</ShapeBtnWrap>
		</StripDiv>
	);
}
